/**
 * Single source of truth for the timezone picker.
 *
 * A curated list of the main zones per continent (with flag emojis and
 * friendly labels) for a clean, scannable dropdown — but made *dynamic*
 * in two ways so it's always correct:
 *   1. Each label carries its LIVE GMT offset, read from the engine's
 *      IANA database (so DST is reflected, e.g. Paris is GMT+1 / GMT+2).
 *   2. The browser's own resolved zone is pinned in a "📍 Detected" group
 *      at the top whenever it isn't already one of the curated entries —
 *      so a browser reporting America/Argentina/Cordoba shows (and
 *      defaults to) Cordoba itself, not a curated stand-in.
 *
 * All ids are valid IANA zones; there is no hand-typed offset to drift.
 */

export interface TimezoneOption {
  /** IANA id, e.g. "America/Argentina/Cordoba". */
  value: string;
  /** Display label, e.g. "🇫🇷 France (Paris) · GMT+2". */
  label: string;
  /** Last path segment, e.g. "Cordoba" — used in inline "it's X in {city}". */
  city: string;
}

export interface TimezoneGroup {
  label: string;
  zones: TimezoneOption[];
}

/** localStorage key used by the (planned) Account → Preferences page
 *  to persist the user's preferred timezone. */
export const TIMEZONE_PREFERENCE_KEY = "prism.user.timezone";

/** The browser's resolved IANA zone (e.g. "America/Argentina/Cordoba"),
 *  or "UTC" when it can't be determined. */
export function getLocalTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && tz.length > 0) return tz;
  } catch {
    // ignore
  }
  return "UTC";
}

/** Browser local zone, resolved once — it never changes within a session. */
export const LOCAL_TIMEZONE = getLocalTimezone();

// Stamped once at module load; offsets are read at "now". A DST shift
// mid-session isn't reflected in the static labels, which is fine for a
// picker — the actual instant is always resolved from the chosen zone +
// wall-clock, independent of the label.
const NOW = new Date();

/** Best-effort GMT offset string for a zone, e.g. "GMT+2", "GMT+5:30".
 *  Empty when the engine can't resolve it. */
function gmtOffset(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortOffset",
    }).formatToParts(NOW);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    if (name && name.length > 0) return name;
  } catch {
    // ignore — label falls back to no offset
  }
  return "";
}

const readable = (tz: string) => tz.replace(/_/g, " ");
const cityOf = (tz: string) => (tz.split("/").pop() ?? tz).replace(/_/g, " ");

/** " · GMT+2" suffix, or "" when the offset can't be resolved. */
function offsetSuffix(tz: string): string {
  const o = gmtOffset(tz);
  return o ? ` · ${o}` : "";
}

/** "America/Argentina/Cordoba (GMT-3)" — used for read-only displays and
 *  as a fallback label for ids that aren't in the curated list. */
export function formatTimezoneLabel(tz: string): string {
  const o = gmtOffset(tz);
  return o ? `${readable(tz)} (${o})` : readable(tz);
}

type CuratedGroup = "America" | "Europe" | "Asia" | "Pacific" | "Africa" | "Other";

interface CuratedEntry {
  value: string;
  /** Flag + friendly name; the live GMT offset is appended at build. */
  label: string;
  city: string;
  group: CuratedGroup;
}

const CURATED_TIMEZONES: CuratedEntry[] = [
  // ── Americas ─────────────────────────────────────────────────────
  { value: "America/New_York", label: "🇺🇸 US/Eastern (New York)", city: "New York", group: "America" },
  { value: "America/Chicago", label: "🇺🇸 US/Central (Chicago)", city: "Chicago", group: "America" },
  { value: "America/Denver", label: "🇺🇸 US/Mountain (Denver)", city: "Denver", group: "America" },
  { value: "America/Los_Angeles", label: "🇺🇸 US/Pacific (Los Angeles)", city: "Los Angeles", group: "America" },
  { value: "America/Toronto", label: "🇨🇦 Canada/Eastern (Toronto)", city: "Toronto", group: "America" },
  { value: "America/Mexico_City", label: "🇲🇽 Mexico (Mexico City)", city: "Mexico City", group: "America" },
  { value: "America/Bogota", label: "🇨🇴 Colombia (Bogotá)", city: "Bogotá", group: "America" },
  { value: "America/Santiago", label: "🇨🇱 Chile (Santiago)", city: "Santiago", group: "America" },
  { value: "America/Sao_Paulo", label: "🇧🇷 Brazil (São Paulo)", city: "São Paulo", group: "America" },
  { value: "America/Argentina/Buenos_Aires", label: "🇦🇷 Argentina (Buenos Aires)", city: "Buenos Aires", group: "America" },
  // ── Europe ───────────────────────────────────────────────────────
  { value: "Europe/London", label: "🇬🇧 UK (London)", city: "London", group: "Europe" },
  { value: "Europe/Paris", label: "🇫🇷 France (Paris)", city: "Paris", group: "Europe" },
  { value: "Europe/Berlin", label: "🇩🇪 Germany (Berlin)", city: "Berlin", group: "Europe" },
  { value: "Europe/Madrid", label: "🇪🇸 Spain (Madrid)", city: "Madrid", group: "Europe" },
  { value: "Europe/Rome", label: "🇮🇹 Italy (Rome)", city: "Rome", group: "Europe" },
  { value: "Europe/Amsterdam", label: "🇳🇱 Netherlands (Amsterdam)", city: "Amsterdam", group: "Europe" },
  { value: "Europe/Zurich", label: "🇨🇭 Switzerland (Zurich)", city: "Zurich", group: "Europe" },
  { value: "Europe/Lisbon", label: "🇵🇹 Portugal (Lisbon)", city: "Lisbon", group: "Europe" },
  { value: "Europe/Athens", label: "🇬🇷 Greece (Athens)", city: "Athens", group: "Europe" },
  { value: "Europe/Istanbul", label: "🇹🇷 Türkiye (Istanbul)", city: "Istanbul", group: "Europe" },
  { value: "Europe/Moscow", label: "🇷🇺 Russia (Moscow)", city: "Moscow", group: "Europe" },
  // ── Asia ─────────────────────────────────────────────────────────
  { value: "Asia/Dubai", label: "🇦🇪 UAE (Dubai)", city: "Dubai", group: "Asia" },
  { value: "Asia/Kolkata", label: "🇮🇳 India (Mumbai)", city: "Mumbai", group: "Asia" },
  { value: "Asia/Singapore", label: "🇸🇬 Singapore", city: "Singapore", group: "Asia" },
  { value: "Asia/Hong_Kong", label: "🇭🇰 Hong Kong", city: "Hong Kong", group: "Asia" },
  { value: "Asia/Shanghai", label: "🇨🇳 China (Shanghai)", city: "Shanghai", group: "Asia" },
  { value: "Asia/Tokyo", label: "🇯🇵 Japan (Tokyo)", city: "Tokyo", group: "Asia" },
  { value: "Asia/Seoul", label: "🇰🇷 South Korea (Seoul)", city: "Seoul", group: "Asia" },
  { value: "Asia/Jerusalem", label: "🇮🇱 Israel (Jerusalem)", city: "Jerusalem", group: "Asia" },
  // ── Pacific ──────────────────────────────────────────────────────
  { value: "Australia/Sydney", label: "🇦🇺 Australia (Sydney)", city: "Sydney", group: "Pacific" },
  { value: "Pacific/Auckland", label: "🇳🇿 New Zealand (Auckland)", city: "Auckland", group: "Pacific" },
  // ── Africa ───────────────────────────────────────────────────────
  { value: "Africa/Casablanca", label: "🇲🇦 Morocco (Casablanca)", city: "Casablanca", group: "Africa" },
  { value: "Africa/Lagos", label: "🇳🇬 Nigeria (Lagos)", city: "Lagos", group: "Africa" },
  { value: "Africa/Johannesburg", label: "🇿🇦 South Africa (Johannesburg)", city: "Johannesburg", group: "Africa" },
  // ── Other ────────────────────────────────────────────────────────
  { value: "UTC", label: "🌐 UTC", city: "UTC", group: "Other" },
];

// Heading text per group, in dropdown order. "Detected" is prepended only
// when the browser zone isn't already curated.
const GROUP_LABEL: Record<string, string> = {
  Detected: "Detected",
  America: "Americas",
  Europe: "Europe",
  Asia: "Asia",
  Pacific: "Pacific",
  Africa: "Africa",
  Other: "Other",
};

const GROUP_ORDER = [
  "Detected",
  "America",
  "Europe",
  "Asia",
  "Pacific",
  "Africa",
  "Other",
];

/** Build the dropdown groups: the curated continents, each label suffixed
 *  with its live GMT offset, plus a "📍 Detected" group pinning the
 *  browser's own zone when it isn't one of the curated entries. */
function buildGroups(): TimezoneGroup[] {
  const byGroup = new Map<string, TimezoneOption[]>();
  for (const e of CURATED_TIMEZONES) {
    const list = byGroup.get(e.group) ?? [];
    list.push({
      value: e.value,
      label: `${e.label}${offsetSuffix(e.value)}`,
      city: e.city,
    });
    byGroup.set(e.group, list);
  }

  const local = LOCAL_TIMEZONE;
  const localIsCurated = CURATED_TIMEZONES.some((e) => e.value === local);
  if (!localIsCurated) {
    byGroup.set("Detected", [
      {
        value: local,
        label: `📍 ${readable(local)}${offsetSuffix(local)}`,
        city: cityOf(local),
      },
    ]);
  }

  const out: TimezoneGroup[] = [];
  for (const groupName of GROUP_ORDER) {
    const list = byGroup.get(groupName);
    if (list && list.length > 0) {
      out.push({ label: GROUP_LABEL[groupName] ?? groupName, zones: list });
    }
  }
  return out;
}

export const TIMEZONE_GROUPS: TimezoneGroup[] = buildGroups();
export const ALL_TIMEZONES: TimezoneOption[] = TIMEZONE_GROUPS.flatMap(
  (g) => g.zones,
);

export function lookupTimezone(
  value: string | null | undefined,
): TimezoneOption | null {
  if (!value) return null;
  return ALL_TIMEZONES.find((z) => z.value === value) ?? null;
}

/** Read the user's preferred timezone for new events. Order:
 *   1. localStorage preference (Account → Preferences)
 *   2. the browser-resolved IANA zone (pinned in "Detected" if not curated)
 *   3. UTC.
 *  A Cordoba browser now defaults to America/Argentina/Cordoba itself. */
export function getDefaultTimezone(): string {
  try {
    const stored = localStorage.getItem(TIMEZONE_PREFERENCE_KEY);
    if (stored && stored.trim().length > 0) return stored.trim();
  } catch {
    // localStorage unavailable — fall through.
  }
  return LOCAL_TIMEZONE;
}

/** Best-effort short timezone abbreviation (EDT, CET, JST, …) for a
 *  given moment in a given zone. */
export function timezoneAbbreviation(date: Date, tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "short",
      hour: "2-digit",
    }).formatToParts(date);
    const name = parts.find((p) => p.type === "timeZoneName")?.value;
    if (name && name.trim().length > 0) return name;
  } catch {
    // ignore
  }
  return tz;
}

/**
 * Build a naive ISO 8601 datetime from `YYYY-MM-DD` + `HH:mm` —
 * `"2026-05-10T14:00:00"`, no `Z`, no offset. Used when sending
 * scheduled events to the backend: the wall-clock the user picked
 * pairs with the timezone field they chose, and the *backend* is
 * responsible for resolving the UTC instant. Avoids any local-zone
 * leakage from the client running in a different IANA zone than
 * the one selected.
 */
export function toNaiveIsoDateTime(
  date: string | null,
  time: string | null,
): string | null {
  if (!date) return null;
  const t = time ?? "23:59";
  const [yyyyStr, mStr, dStr] = date.split("-");
  const [hhStr, mmStr] = t.split(":");
  const yyyy = Number(yyyyStr);
  const m = Number(mStr);
  const d = Number(dStr);
  const hh = Number(hhStr);
  const mm = Number(mmStr);
  if (
    !Number.isFinite(yyyy) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${yyyy}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
}

/** TZ-aware version of `combineDateTime`. Trick: take the wall-clock
 *  as if it were UTC, format it in `tz`, and read back what the zone
 *  shows. The delta between what we wanted and what we got is the
 *  zone's offset for that instant — subtract to land on the right UTC
 *  moment. Handles DST transitions because we use the offset *at the
 *  target time*. */
export function combineDateTimeInTz(
  date: string | null,
  time: string | null,
  tz: string,
): string | null {
  if (!date) return null;
  const t = time ?? "23:59";
  const [hh, mm] = t.split(":").map((s) => Number(s));
  const [yyyy, m, d] = date.split("-").map((s) => Number(s));
  if (
    !Number.isFinite(yyyy) ||
    !Number.isFinite(m) ||
    !Number.isFinite(d) ||
    !Number.isFinite(hh) ||
    !Number.isFinite(mm)
  ) {
    return null;
  }
  const utcGuess = Date.UTC(yyyy, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0);
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    const parts = fmt.formatToParts(new Date(utcGuess));
    const get = (type: string) =>
      Number(parts.find((p) => p.type === type)?.value ?? 0);
    const tzAsUtc = Date.UTC(
      get("year"),
      get("month") - 1,
      get("day"),
      get("hour"),
      get("minute"),
      get("second"),
    );
    const offset = tzAsUtc - utcGuess;
    return new Date(utcGuess - offset).toISOString();
  } catch {
    // Invalid/legacy zone id — treat the wall-clock as UTC so scheduling
    // still produces a usable instant instead of throwing.
    return new Date(utcGuess).toISOString();
  }
}

/** Read back the wall-clock {date,time} representation of a UTC ISO
 *  inside a target timezone. */
export function splitDateTimeInTz(
  iso: string | null,
  tz: string,
): { date: string; time: string } {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { date: "", time: "" };
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  };
  // Invalid/legacy zone id — fall back to UTC instead of throwing.
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      ...opts,
      timeZone: tz,
    }).formatToParts(d);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", {
      ...opts,
      timeZone: "UTC",
    }).formatToParts(d);
  }
  const get = (type: string) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
}

/** "May 10, 2026 at 2:00 PM EDT" in the chosen timezone. */
export function formatDateTimeInTz(
  iso: string | Date,
  tz: string,
): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const opts: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  };
  try {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(d);
  } catch {
    // Invalid/legacy zone id — degrade to UTC rather than crash the render.
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).format(
      d,
    );
  }
}

/** Just the time + abbreviation, for tight surfaces. */
export function formatTimeInTz(iso: string | Date, tz: string): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const opts: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  };
  try {
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: tz }).format(d);
  } catch {
    // Invalid/legacy zone id — degrade to UTC rather than crash the render.
    return new Intl.DateTimeFormat("en-US", { ...opts, timeZone: "UTC" }).format(
      d,
    );
  }
}
